import { DEFAULT_MILESTONES, StatusAvatarLookup } from './milestones';
import { StreakSnapshot } from './types';

/** Noon CT on 2026-11-09, well clear of last call. */
export const NOON_CT = Date.UTC(2026, 10, 9, 18, 0, 0);
/** 22:08 CT on 2026-11-09: 1h 52m to midnight, last call. */
export const LAST_CALL_CT = Date.UTC(2026, 10, 10, 4, 8, 0);
export const TODAY = '20261109';
export const YESTERDAY = '20261108';

/** Launch art per status set (EN-11154), for stories and tests. */
export const FIXTURE_AVATAR_COUNTS: Record<string, number> = {
  cowboy: 6,
  grinder: 5,
  marshal: 4,
  baron: 3,
  legend: 1
};

/**
 * A manifest lookup with the launch counts. The URLs are empty, so the banner
 * draws its placeholder discs; pass `src` for a picture.
 */
export function fixtureStatusAvatars(src = ''): StatusAvatarLookup {
  return (setKey) => Array(FIXTURE_AVATAR_COUNTS[setKey ?? ''] ?? 0).fill(src);
}

/** The pool manifest as EN-11264 ships it (platform#4218), for the picker. */
export const FIXTURE_AVATAR_MANIFEST = {
  avatars: ['001-saloon.png', '002-bank.png', '003-whisky.png'],
  status: {
    cowboy: [
      'cowboy-1-bandana.png',
      'cowboy-2-spurs.png',
      'cowboy-3-canteen.png',
      'cowboy-4-campfire.png',
      'cowboy-5-lasso.png',
      'cowboy-6-vaquero.png'
    ],
    grinder: [
      'grinder-1-anvil.png',
      'grinder-2-pickaxes.png',
      'grinder-3-grindstone.png',
      'grinder-4-lamp.png',
      'grinder-5-smith.png'
    ],
    marshal: [
      'marshal-1-star.png',
      'marshal-2-watch.png',
      'marshal-3-compass.png',
      'marshal-4-lawman.png'
    ],
    baron: ['baron-1-longhorn.png', 'baron-2-rattlesnake.png', 'baron-3-tycoon.png'],
    legend: ['legend-1-immortal.png']
  }
};

export function makeSnapshot(over: Partial<StreakSnapshot> = {}): StreakSnapshot {
  return {
    version: 1,
    asOf: NOON_CT,
    day: TODAY,
    status: 'live',
    currentStreak: 8,
    bestStreak: 12,
    lastQualifiedDay: YESTERDAY,
    flopsToday: 1,
    flopsNeeded: 2,
    freezeBalance: 2,
    promoFreezes: 1,
    cashFreezes: 0,
    milestones: DEFAULT_MILESTONES,
    currentMilestoneKey: 'cowboy',
    highestMilestoneKey: 'cowboy',
    showFrame: true,
    lastFreezeUsedDay: null,
    ...over
  };
}

/** One snapshot per Phase 1 state on canvas board A, for stories and tests. */
export const STATE_FIXTURES: Record<string, StreakSnapshot> = {
  'No streak yet': makeSnapshot({
    status: 'none',
    currentStreak: 0,
    bestStreak: 0,
    lastQualifiedDay: null,
    flopsToday: 0,
    freezeBalance: 0,
    promoFreezes: 0,
    currentMilestoneKey: null,
    highestMilestoneKey: null
  }),
  'In progress': makeSnapshot(),
  Counted: makeSnapshot({ currentStreak: 9, lastQualifiedDay: TODAY, flopsToday: 2 }),
  'Milestone day': makeSnapshot({
    currentStreak: 30,
    bestStreak: 30,
    lastQualifiedDay: TODAY,
    flopsToday: 2,
    freezeBalance: 3,
    currentMilestoneKey: 'grinder',
    highestMilestoneKey: 'grinder',
    notice: { milestoneReached: { key: 'grinder', day: TODAY } }
  }),
  'Freeze covered last night': makeSnapshot({
    status: 'frozen',
    currentStreak: 20,
    bestStreak: 20,
    lastQualifiedDay: '20261107',
    flopsToday: 0,
    freezeBalance: 1,
    currentMilestoneKey: 'cowboy',
    highestMilestoneKey: 'cowboy',
    lastFreezeUsedDay: YESTERDAY,
    notice: { freezeUsedFor: YESTERDAY }
  }),
  Ended: makeSnapshot({
    status: 'ended',
    currentStreak: 0,
    bestStreak: 12,
    lastQualifiedDay: '20261107',
    flopsToday: 0,
    currentMilestoneKey: null,
    highestMilestoneKey: 'cowboy',
    notice: { ended: { length: 12, day: YESTERDAY } }
  }),
  'Back to day 1': makeSnapshot({
    currentStreak: 1,
    bestStreak: 12,
    lastQualifiedDay: TODAY,
    flopsToday: 2,
    freezeBalance: 1,
    promoFreezes: 0,
    currentMilestoneKey: null,
    highestMilestoneKey: 'cowboy',
    notice: { streakStarted: { day: TODAY, freezes: 1 } }
  }),
  'Past 365': makeSnapshot({
    currentStreak: 412,
    bestStreak: 412,
    currentMilestoneKey: 'legend',
    highestMilestoneKey: 'legend'
  })
};
